/**
 * Payment Service
 *
 * Handles all communication with the backend payment API for Wompi integration.
 */

import apiService from './api';
import type {
  CreateTransactionRequest,
  CreateTransactionResponse,
  TransactionReference,
  AcceptanceTokenResponse,
  TransactionVerificationResponse,
  Payment,
  PaymentHistoryResponse,
} from '../types/payment';

/**
 * Payment Service Class
 */
class PaymentService {
  private readonly baseEndpoint = '/payments';

  /**
   * Create a new transaction reference
   * This creates a Payment record in the backend and returns the reference
   * needed to initialize the Wompi Widget
   *
   * @param data - Transaction creation request data
   * @returns Transaction reference data or null if failed
   * @throws Error if request fails
   */
  async createTransaction(data: CreateTransactionRequest): Promise<TransactionReference> {
    try {
      const response = await apiService.post<CreateTransactionResponse>(
        `${this.baseEndpoint}/create`,
        data
      );

      if (!response.success || !response.data) {
        throw new Error(response.message || 'Failed to create transaction');
      }

      return response.data;
    } catch (error) {
      console.error('Error creating transaction:', error);
      throw error;
    }
  }

  /**
   * Get Wompi acceptance token
   * This token is required by Wompi to accept their terms and conditions
   *
   * @returns Acceptance token data
   * @throws Error if request fails
   */
  async getAcceptanceToken(): Promise<AcceptanceTokenResponse> {
    try {
      const response = await apiService.get<AcceptanceTokenResponse>(
        `${this.baseEndpoint}/acceptance-token`
      );
      return response;
    } catch (error) {
      console.error('Error fetching acceptance token:', error);
      throw error;
    }
  }

  /**
   * Verify transaction status by reference
   * This checks the current status of a transaction after payment
   *
   * @param reference - Unique transaction reference
   * @returns Payment data or null if not found
   * @throws Error if request fails
   */
  async verifyTransaction(reference: string): Promise<Payment | null> {
    try {
      const response = await apiService.get<TransactionVerificationResponse>(
        `${this.baseEndpoint}/verify/${reference}`
      );

      if (!response.success || !response.payment) {
        return null;
      }

      return response.payment;
    } catch (error) {
      console.error('Error verifying transaction:', error);
      throw error;
    }
  }

  /**
   * Verify transaction status by reference (Public - no auth required)
   * Used for payment redirects from Wompi checkout
   *
   * @param reference - Unique transaction reference
   * @returns Payment data or null if not found
   * @throws Error if request fails
   */
  async verifyTransactionPublic(reference: string): Promise<Payment | null> {
    try {
      const response = await apiService.get<{ success: boolean; data?: Payment; message?: string }>(
        `${this.baseEndpoint}/verify-public/${reference}`
      );

      if (!response.success || !response.data) {
        return null;
      }

      return response.data;
    } catch (error) {
      console.error('Error verifying transaction (public):', error);
      throw error;
    }
  }

  /**
   * Get payment by ID
   *
   * @param id - Payment ID
   * @returns Payment data
   * @throws Error if request fails
   */
  async getPaymentById(id: number): Promise<Payment> {
    try {
      const response = await apiService.get<{ data: Payment }>(
        `${this.baseEndpoint}/${id}`
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching payment:', error);
      throw error;
    }
  }

  /**
   * Get user's payment history
   * Returns paginated list of all payments for the authenticated user
   *
   * @param page - Page number (default: 1)
   * @param perPage - Items per page (default: 10)
   * @returns Paginated payment history
   * @throws Error if request fails
   */
  async getPaymentHistory(page: number = 1, perPage: number = 10): Promise<PaymentHistoryResponse> {
    try {
      const response = await apiService.get<PaymentHistoryResponse>(
        `${this.baseEndpoint}?page=${page}&per_page=${perPage}`
      );
      return response;
    } catch (error) {
      console.error('Error fetching payment history:', error);
      throw error;
    }
  }

  /**
   * Poll transaction status
   * Continuously checks transaction status until it's finalized or timeout
   *
   * @param reference - Transaction reference
   * @param maxAttempts - Maximum polling attempts (default: 20)
   * @param interval - Interval between attempts in ms (default: 3000)
   * @returns Final payment status or null if timeout
   */
  async pollTransactionStatus(
    reference: string,
    maxAttempts: number = 20,
    interval: number = 3000
  ): Promise<Payment | null> {
    let attempts = 0;

    const poll = async (): Promise<Payment | null> => {
      try {
        const payment = await this.verifyTransaction(reference);

        if (!payment) {
          return null;
        }

        // Check if payment is in a final state
        const finalStates = ['APPROVED', 'DECLINED', 'ERROR', 'VOIDED'];
        if (finalStates.includes(payment.status)) {
          return payment;
        }

        // Continue polling if not in final state and attempts remain
        attempts++;
        if (attempts < maxAttempts) {
          await new Promise(resolve => setTimeout(resolve, interval));
          return poll();
        }

        // Timeout - return current state
        return payment;
      } catch (error) {
        console.error('Error polling transaction status:', error);
        attempts++;
        if (attempts < maxAttempts) {
          await new Promise(resolve => setTimeout(resolve, interval));
          return poll();
        }
        return null;
      }
    };

    return poll();
  }

  /**
   * Validate payment data before sending to backend
   *
   * @param data - Transaction data to validate
   * @returns Validation result
   */
  validateTransactionData(data: CreateTransactionRequest): { valid: boolean; errors: string[] } {
    const errors: string[] = [];

    // Validate amount
    if (!data.amount || data.amount <= 0) {
      errors.push('Amount must be greater than 0');
    }

    // Validate currency
    if (!data.currency) {
      errors.push('Currency is required');
    }

    // Validate customer email
    if (!data.customer_email || !this.isValidEmail(data.customer_email)) {
      errors.push('Valid customer email is required');
    }

    // Validate customer name
    if (!data.customer_name || data.customer_name.trim().length < 2) {
      errors.push('Customer name is required (min 2 characters)');
    }

    // Validate phone if provided
    if (data.customer_phone && !this.isValidPhone(data.customer_phone)) {
      errors.push('Invalid phone number format');
    }

    return {
      valid: errors.length === 0,
      errors,
    };
  }

  /**
   * Email validation helper
   */
  private isValidEmail(email: string): boolean {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }

  /**
   * Phone validation helper (basic validation)
   */
  private isValidPhone(phone: string): boolean {
    const phoneRegex = /^\+?[\d\s\-()]{8,}$/;
    return phoneRegex.test(phone);
  }

  /**
   * Format amount to cents for Wompi (amount * 100)
   *
   * @param amount - Amount in currency units
   * @returns Amount in cents
   */
  formatAmountToCents(amount: number): number {
    return Math.round(amount * 100);
  }

  /**
   * Format amount from cents to currency units (cents / 100)
   *
   * @param amountInCents - Amount in cents
   * @returns Amount in currency units
   */
  formatAmountFromCents(amountInCents: number): number {
    return amountInCents / 100;
  }

  /**
   * Get human-readable payment status
   *
   * @param status - Payment status enum value
   * @returns Human-readable status
   */
  getStatusLabel(status: string): string {
    const statusLabels: Record<string, string> = {
      PENDING: 'Pendiente',
      APPROVED: 'Aprobado',
      DECLINED: 'Rechazado',
      ERROR: 'Error',
      VOIDED: 'Anulado',
    };

    return statusLabels[status] || status;
  }

  /**
   * Get status color for UI display
   *
   * @param status - Payment status
   * @returns Color code or class name
   */
  getStatusColor(status: string): string {
    const statusColors: Record<string, string> = {
      PENDING: 'warning',
      APPROVED: 'success',
      DECLINED: 'error',
      ERROR: 'error',
      VOIDED: 'default',
    };

    return statusColors[status] || 'default';
  }
}

// Export singleton instance
const paymentService = new PaymentService();
export default paymentService;
